import React, { useCallback, useRef, useState } from 'react';

import { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';

import { TRACKING_NUMBER_OPTIONS } from '@/constants';

import './TrackingNumberDropdown.css';

interface TrackingNumberDropdownProps extends ICellRendererParams {
  setValue: (value: string) => void | Promise<void>;
}

export const TrackingNumberDropdown = (props: TrackingNumberDropdownProps) => {
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const dropdownRef = useRef<Dropdown>(null);
  const isTrackingNumberUpdateEnabled = Boolean(
    props.context?.isTrackingNumberUpdateEnabled,
  );

  const invalidTrackingValues = [
    'Client PU',
    'TOG PU/Delivery',
    'Courier',
    'Tracking Number',
    undefined,
  ];
  const isValidTrackingNumber = !invalidTrackingValues.includes(
    props.data.trackingNumber,
  );
  const [selectedOption, setSelectedOption] = useState<string | null>(() => {
    return isValidTrackingNumber
      ? 'Tracking Number'
      : (props.data.trackingNumber ?? null);
  });
  const [trackingNumber, setTrackingNumber] = useState<string>(
    props.data.trackingNumber && selectedOption === 'Tracking Number'
      ? props.data.trackingNumber
      : '',
  );
  const [options, setOptions] = useState(() => {
    if (props.data.trackingNumber && isValidTrackingNumber) {
      return TRACKING_NUMBER_OPTIONS.map((o) =>
        o.value === 'Tracking Number'
          ? { ...o, label: props.data.trackingNumber }
          : o,
      );
    }
    return TRACKING_NUMBER_OPTIONS;
  });

  const handleOptionChange = (e: DropdownChangeEvent) => {
    e.stopPropagation();
    setSelectedOption(e.value);
    if (e.value !== 'Tracking Number') {
      overlayPanelRef.current?.hide();
    }
  };

  const handleSubmit = async (option: string) => {
    let newValue = '';
    if (option === 'Tracking Number') {
      setSelectedOption('Tracking Number');
      if (trackingNumber) {
        newValue = trackingNumber;
      }
    } else {
      newValue = option;
    }

    if (newValue) {
      await props.setValue(newValue);
      props.api.stopEditing();
      overlayPanelRef.current?.hide();
    }
  };

  const handleTrackingNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newTrackingNumber = e.target.value;
    setTrackingNumber(newTrackingNumber);
    setSelectedOption('Tracking Number');

    setOptions((prevOptions) =>
      prevOptions.map((option) =>
        option.value === 'Tracking Number'
          ? {
              ...option,
              label: newTrackingNumber || 'Tracking Number',
            }
          : option,
      ),
    );
  };

  const handleInputClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div className="tracking-number-dropdown-spacer">
      <Dropdown
        ref={dropdownRef}
        className="tracking-number-dropdown"
        value={selectedOption}
        options={options || []}
        optionLabel="label"
        placeholder="Select An Option"
        onChange={handleOptionChange}
        onShow={() => {
          overlayPanelRef.current?.show(
            null,
            dropdownRef.current?.getElement(),
          );
        }}
        panelClassName="hidden"
      />
      <OverlayPanel
        ref={overlayPanelRef}
        className="tracking-number-dropdown-panel"
      >
        {options.map((option) => (
          <div
            key={option.value}
            className={`tracking-number-dropdown-item ${
              selectedOption === option.value ? 'selected' : ''
            }`}
            onClick={async () => {
              if (
                !isTrackingNumberUpdateEnabled ||
                option.value === 'Tracking Number'
              ) {
                setSelectedOption(option.value);
              }
              await handleSubmit(option.value);
            }}
          >
            <div className="tracking-number-dropdown-scroll">
              <span>{option.label}</span>
            </div>
          </div>
        ))}
        {selectedOption === 'Tracking Number' && (
          <div
            className="tracking-number-input__wrapper"
            onClick={handleInputClick}
          >
            <InputText
              value={trackingNumber}
              onChange={handleTrackingNumberChange}
              placeholder="Enter tracking number"
            />
            <Button
              label="Submit"
              severity="secondary"
              onClick={() => handleSubmit(selectedOption)}
              disabled={!selectedOption}
            />
          </div>
        )}
      </OverlayPanel>
    </div>
  );
};
